import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import clsx from 'clsx';
import React, { Dispatch, Fragment } from 'react';

import { NonLinearStepType } from '../NonLinearStepper';
import styles from './NonLinearStep.module.scss';

interface NonLinearStepProps {
  index: number;
  step: NonLinearStepType;
  activeIndex?: number;
  iconSize: number;
  shouldHighlightStep: boolean;
  shouldHighlightConnector: boolean;
  indexErrorMap: Record<number, boolean>;
  setIndexErrorMap: Dispatch<
    React.SetStateAction<{
      [key: number]: boolean;
    }>
  >;
}

export const NonLinearStep = ({
  index,
  activeIndex,
  step,
  iconSize,
  shouldHighlightConnector,
  shouldHighlightStep,
  indexErrorMap,
  setIndexErrorMap,
}: NonLinearStepProps) => {
  const { hoverLabel, logoUrl, imgNode, connectorLabel, iconColor } = step;
  const isActiveStep = index === activeIndex;

  const sizeStyle = { minWidth: iconSize, height: iconSize };

  return (
    <Fragment key={index}>
      <TooltipContainer
        label={hoverLabel}
        placement="top"
        disabled={!hoverLabel}
      >
        <div
          className={clsx(styles.stepContainer, {
            [styles.active]: shouldHighlightStep,
          })}
          aria-current={isActiveStep ? 'step' : undefined}
          data-testid={`step-${hoverLabel}`}
        >
          {imgNode ? (
            <div
              className={styles.customLogo}
              style={{
                ...sizeStyle,
                ...(iconColor ? { backgroundColor: iconColor } : undefined),
              }}
            >
              {imgNode}
            </div>
          ) : indexErrorMap[index] || !logoUrl ? (
            <div
              className={clsx(styles.fallbackLogo, {
                [styles.active]: shouldHighlightStep,
              })}
              data-testid={`fallback-logo-${hoverLabel}`}
              style={sizeStyle}
            >
              {hoverLabel.charAt(0).toUpperCase()}
            </div>
          ) : (
            <img
              className={styles.stepLogo}
              data-testid={`logo-${hoverLabel}`}
              key={index}
              onError={() =>
                setIndexErrorMap(prev => ({ ...prev, [index]: true }))
              }
              src={logoUrl}
              alt={hoverLabel}
              width={iconSize}
              height={iconSize}
              style={{
                ...sizeStyle,
                ...(iconColor
                  ? { backgroundColor: iconColor }
                  : undefined),
              }}
            />
          )}
        </div>
      </TooltipContainer>
      {connectorLabel && (
        <>
          <div
            className={clsx(styles.dottedLine, {
              [styles.active]: shouldHighlightConnector,
            })}
          />
          <div
            className={clsx(styles.connector, {
              [styles.active]: shouldHighlightConnector,
            })}
          >
            {connectorLabel}
          </div>
          <div
            className={clsx(styles.dottedLine, {
              [styles.active]: shouldHighlightConnector,
            })}
          />
        </>
      )}
    </Fragment>
  );
};
